import { CurrencyPipe } from '@angular/common';
import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { EComandaProductoEstado } from '@core/enums/comanda.enum';
import {
  ActualizarEstadoProductoComandaReq,
  ComandaItemRes,
  EliminarProductoComandaReq,
} from '@core/interfaces/request-response/comanda.dto';
import { ComandaApiService } from '@core/services/api/comanda-api.service';
import { ToastService } from '@shared/services/toast.service';
import { estadoProductoEnumToString } from '@shared/utils/functions.utils';

@Component({
  selector: 'app-comanda-card-producto',
  imports: [CurrencyPipe],
  templateUrl: './comanda-card-producto.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './comanda-card-producto.css',
})
export class ComandaCardProducto {
  // servicios
  private readonly comandaApiService = inject(ComandaApiService);
  private readonly toastService = inject(ToastService);
  // input
  mesa_id = input<number>();
  producto = input.required<ComandaItemRes>();

  EComandaProductoEstado = EComandaProductoEstado;

  // metodos
  estadoProducto = computed(() => {
    return estadoProductoEnumToString(this.producto().estado);
  });

  puedeAvanzarEstado = computed(() => {
    return ![EComandaProductoEstado.ENTREGADO, EComandaProductoEstado.CANCELADO].includes(
      this.producto().estado,
    );
  });

  botonAvanzarEstadoTexto = computed((): string => {
    switch (this.producto().estado) {
      case EComandaProductoEstado.PENDIENTE:
        return 'Preparar';
      case EComandaProductoEstado.EN_PREPARACION:
        return 'Listo';
      case EComandaProductoEstado.LISTO:
        return 'Entregar';
      case EComandaProductoEstado.ENTREGADO:
        return 'Entregado';
      case EComandaProductoEstado.CANCELADO:
        return 'Cancelado';
      default:
        return 'Siguiente Estado';
    }
  });

  estadoProgreso = computed(() => {
    switch (this.producto().estado) {
      case EComandaProductoEstado.PENDIENTE:
        return 25;
      case EComandaProductoEstado.EN_PREPARACION:
        return 55;
      case EComandaProductoEstado.LISTO:
        return 80;
      case EComandaProductoEstado.ENTREGADO:
      case EComandaProductoEstado.CANCELADO:
        return 100;
      default:
        return 0;
    }
  });

  eliminarProducto() {
    if (this.mesa_id() === null) return;
    const rq: EliminarProductoComandaReq = {
      mesa_id: this.mesa_id()!,
      comanda_item_id: this.producto().id,
    };
    this.comandaApiService.eliminarProductoComanda(rq).subscribe({
      next: (res) => {
        if (!res.success) {
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            res.message || 'No se pudo eliminar el producto de la comanda.',
          );
          return;
        }
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          'Producto eliminado de la comanda correctamente.',
        );
      },
      error: (err) => {
        if (err.status === 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudo eliminar el producto de la comanda.',
        );
      },
    });
  }
  avanzarEstadoProducto() {
    if (this.mesa_id() === null) return;
    const rq: ActualizarEstadoProductoComandaReq = {
      mesa_id: this.mesa_id()!,
      comandas_items_ids: [this.producto().id],
      estado: this.siguienteEstado(this.producto().estado),
    };
    this.apiActualizarEstado(rq);
  }

  private apiActualizarEstado(rq: ActualizarEstadoProductoComandaReq) {
    this.comandaApiService.actualizarEstadoProductoComanda(rq).subscribe({
      next: (rs) => {
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          rs.message || 'Estado del producto actualizado correctamente.',
        );
      },
      error: (err) => {
        console.error('Error al actualizar el estado del producto:', err);
        if (err.status === 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudo actualizar el estado del producto.',
        );
      },
    });
  }

  private siguienteEstado(estado: EComandaProductoEstado): EComandaProductoEstado {
    switch (estado) {
      case EComandaProductoEstado.PENDIENTE:
        return EComandaProductoEstado.EN_PREPARACION;
      case EComandaProductoEstado.EN_PREPARACION:
        return EComandaProductoEstado.LISTO;
      case EComandaProductoEstado.LISTO:
        return EComandaProductoEstado.ENTREGADO;
      default:
        return estado;
    }
  }
}
